// ---------------------------------------------------------------------------
// D3-D4: API Hook — fetch with auth, loading/error states
// ---------------------------------------------------------------------------
// Uses cookie-based auth (Better Auth HttpOnly cookies) via credentials: include.
// Base URL from VITE_API_URL. Delegates to apiFetch for consistent auth handling.

import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { subscribeActiveTenantContextChanged } from "@/lib/active-tenant-context";

interface UseApiResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApi<T>(
  path: string,
  // NOTE: `mock` is accepted for backward compatibility but NEVER used.
  // The hook always makes a real API call. Callers passing mock data get
  // no special behavior — the mock value is silently ignored.
  options?: {
    activeTenantId?: string | null;
    enabled?: boolean;
    mock?: T;
    skipActiveTenantHeader?: boolean;
  },
): UseApiResult<T> {
  const enabled = options?.enabled !== false && path !== "";
  const shouldRefetchOnTenantContextChange =
    enabled && !options?.skipActiveTenantHeader && options?.activeTenantId === undefined;
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [tenantContextVersion, setTenantContextVersion] = useState(0);

  useEffect(() => {
    if (!shouldRefetchOnTenantContextChange) {
      return;
    }

    return subscribeActiveTenantContextChanged(() => {
      setTenantContextVersion((version) => version + 1);
    });
  }, [shouldRefetchOnTenantContextChange]);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const json = await apiFetch<Record<string, unknown>>(path, {
        activeTenantId: options?.activeTenantId,
        signal,
        skipActiveTenantHeader: options?.skipActiveTenantHeader,
      });
      // API responses wrap data in { data: T }. Unwrap if present.
      const result = (json && typeof json === "object" && "data" in json ? json.data : json) as T;
      setData(result);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setData(null);
      const msg = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error ? err.message : "Unknown error";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [path, enabled, options?.activeTenantId, options?.skipActiveTenantHeader, tenantContextVersion]);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  return { data, isLoading, error, refetch: () => fetchData() };
}

export function useApiMutation<TInput, TResult>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE" = "POST",
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TResult | null>(null);

  const mutate = useCallback(async (input: TInput): Promise<TResult | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiFetch<TResult>(path, {
        method,
        body: JSON.stringify(input),
      });
      setData(result);
      return result;
    } catch (err) {
      const msg = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error ? err.message : "Unknown error";
      setError(msg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [path, method]);

  return { mutate, isLoading, error, data };
}
